import { flattenDeep, reduce } from 'lodash';
import { Action } from 'redux';

export enum RequestActionTypes {
  REQUEST = 'REQUEST',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}

export enum LoadingStatus {
  initial = 'initial',
  loading = 'loading',
  success = 'success',
  failure = 'failure',
}

export interface AppAction<T> extends Action {
  payload: T;
}

const actionTypeState = {
  [RequestActionTypes.REQUEST]: LoadingStatus.loading,
  [RequestActionTypes.SUCCESS]: LoadingStatus.success,
  [RequestActionTypes.FAILURE]: LoadingStatus.failure,
};

export const createActionType = (...parts: any) => flattenDeep(parts).join('_');

export const createActionCreator = <T>(...type: string[]) => {
  return (payload?: T) => ({
    type: createActionType(type),
    payload,
  });
};

export const createApiActionCreators = <T>(type: string) => ({
  request: createActionCreator<T>(type, RequestActionTypes.REQUEST),
  success: createActionCreator(type, RequestActionTypes.SUCCESS),
  failure: createActionCreator(type, RequestActionTypes.FAILURE),
});

export const createReducer = (initialState: any, reducerMap: any) => {
  const iterator: any = (reducers: any, initial: any = {}, prefix: any = []) =>
    reduce(
      reducers,
      (acc: any, reducer: any, type: string) => {
        if (typeof reducer === 'function') {
          return {
            ...acc,
            [createActionType(prefix, type)]: reducer,
          };
        }
        return iterator(reducer, acc, [createActionType(prefix, type)] as never[]);
      },
      initial
    );

  const flattened = iterator(reducerMap);

  return (state = initialState, action: any) => {
    const reducer = flattened[action.type];
    return reducer ? reducer(state, action.payload) : state;
  };
};

export const createLoadingStateReducer = (
  initialState: any,
  statesUpdate: Record<string, RequestActionTypes[]>,
  additionalReducerMap?: any
) => {
  const reducerMap = Object.keys(statesUpdate).reduce(
    (actions, key) => ({
      ...actions,
      [key]: statesUpdate[key].reduce(
        (actionTypes, actionType) => ({
          ...actionTypes,
          [actionType]: (_state: LoadingStatus) => actionTypeState[actionType],
        }),
        {}
      ),
    }),
    additionalReducerMap || {}
  );

  return createReducer(initialState, reducerMap);
};

export const CLEAR_REDUX_STORE = 'CLEAR_REDUX_STORE';

export const clearReduxStore = () => ({
  type: CLEAR_REDUX_STORE,
});
